// Standard fixed-rate amortisation.  Per ECONOMY §8:
//   weekly payment = principal × rate / (1 − (1+rate)^−term)
// Implemented as a pure function so the loan reducer and the Cashflow Forecast tool
// can both call it without going through state.

export function weeklyLoanPayment(principal: number, ratePerWeek: number, termWeeks: number): number {
  if (principal <= 0) return 0;
  if (termWeeks <= 0) return principal;
  if (ratePerWeek <= 0) return principal / termWeeks;
  const denom = 1 - Math.pow(1 + ratePerWeek, -termWeeks);
  if (denom <= 0) return principal / termWeeks; // pathological
  return (principal * ratePerWeek) / denom;
}

export type LoanPaymentBreakdown = {
  payment: number;
  interestPortion: number;
  principalPortion: number;
  remainingPrincipal: number;
};

/** Apply one weekly payment to a loan. Pure — caller stores the new remainingPrincipal. */
export function applyOnePayment(
  remainingPrincipal: number,
  ratePerWeek: number,
  weeklyPayment: number,
): LoanPaymentBreakdown {
  const interestPortion = remainingPrincipal * ratePerWeek;
  let payment = weeklyPayment;
  // If the scheduled payment overshoots what's left + interest, clip it.
  const fullPayoff = remainingPrincipal + interestPortion;
  if (payment > fullPayoff) payment = fullPayoff;
  const principalPortion = payment - interestPortion;
  const newRemaining = Math.max(0, remainingPrincipal - principalPortion);
  return {
    payment,
    interestPortion,
    principalPortion,
    remainingPrincipal: newRemaining,
  };
}
